import { z } from '@pkg/form-builder/zod';
import type { ZodAny, ZodArray, ZodString, ZodType } from '@pkg/form-builder/zod';
import { type Component, markRaw } from 'vue';
import {
  FormFieldRadios,
  FormFieldCheckboxes,
  FormFieldNote,
  FormFieldSelect,
  FormFieldText,
  FormFieldTextarea,
  FormFieldFile,
  FormFieldPhone,
} from '@pkg/ui/components/ui/form/fields';
import type { HTMLInputTypeAttribute } from '@pkg/types/html';
import { type FormFieldObject, type FormFieldOptions, INPUT_TYPE } from '@pkg/sanity-utils/types/forms';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {toHTML } from '@portabletext/to-html'
import { MARKS } from '@pkg/config/sanity/portableEditor';

// Mark vue components as never needing to be proxied - slight speed increase
markRaw(FormFieldRadios);
markRaw(FormFieldCheckboxes);
markRaw(FormFieldNote);
markRaw(FormFieldSelect);
markRaw(FormFieldText);
markRaw(FormFieldTextarea);
markRaw(FormFieldFile);
markRaw(FormFieldPhone);

export function inputTypeToComponent(inputType: INPUT_TYPE) {
  const map: { [key in INPUT_TYPE]: Component } = {
    [INPUT_TYPE.TEXT]: FormFieldText,
    [INPUT_TYPE.TEXT_AREA]: FormFieldTextarea,
    [INPUT_TYPE.EMAIL]: FormFieldText,
    [INPUT_TYPE.PHONE]: FormFieldPhone,
    [INPUT_TYPE.DROPDOWN]: FormFieldSelect,
    [INPUT_TYPE.COUNTRY_SELECT]: FormFieldSelect,
    [INPUT_TYPE.BLOCKER_SELECT]: FormFieldSelect,
    [INPUT_TYPE.CHECKBOX]: FormFieldCheckboxes,
    [INPUT_TYPE.RADIOS]: FormFieldRadios,
    [INPUT_TYPE.FILE]: FormFieldFile,
    [INPUT_TYPE.NOTE]: FormFieldNote,
  };

  return map[inputType];
}

export function inputTypeToType(inputType: INPUT_TYPE) {
  const map: { [key in INPUT_TYPE]?: HTMLInputTypeAttribute } = {
    [INPUT_TYPE.TEXT]: 'text',
    [INPUT_TYPE.EMAIL]: 'email',
    [INPUT_TYPE.PHONE]: 'tel',
  };

  return (inputType in map)
    ? map[inputType]
    : undefined;
}

// @todo These should come from the CMS, per field, under validation.
const MAX_FILE_SIZE = 500000;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const serializers = {
  marks: {
    [MARKS.PAGE_LINK]: ({ value, children }: any) => {
      return `<a href="/${value?.reference?.locale || ''}${value?.reference?.slug || ''}" class="rich-validation-text" rel="noopener noreferrer">${children}</a>`;
    },
    [MARKS.URL_LINK]: ({ value, children }: any) => {
      return `<a href="${value?.href || ''}" class="rich-validation-text" rel="noopener noreferrer" target="_blank">${children}</a>`;
    },
  },
};

export function fieldToValidation(formField: FormFieldObject) {
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;

  const map: {
    [key in INPUT_TYPE]: {
      base: ZodType;
      required: (z: any) => any;
      optional: (z: any) => any;
      fileTypes?: (z: any) => any;
      maxSize?: (z: any) => any;
    }
  } = {

    [INPUT_TYPE.TEXT]: {
      base: z.string(),
      required: (z: ZodString) => z.min(1, formField.validation?.required),
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.TEXT_AREA]: {
      base: z.string(),
      required: (z: ZodString) => z.min(1, formField.validation?.required),
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.EMAIL]: {
      base: z.string().email(),
      required: (z: ZodString) => z.min(1, formField.validation?.required),
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.PHONE]: {
      base: z.string().refine((value) => isValidPhoneNumber(value|| ''), {
        message: t('zodI18n.validations.invalid_phone'),
      }),
      required: (z: ZodString) => z,
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.DROPDOWN]: {
      base: z.string(),
      required: (z: ZodString) => z.min(1, formField.validation?.required),
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.COUNTRY_SELECT]: {
      base: z.string(),
      required: (z: ZodString) => z.min(1, formField.validation?.required),
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.BLOCKER_SELECT]: {
      base: z.string(),
      required: (z: ZodString) => z.refine(
        (val) => {
          return val === 'true';
        },{
        message: formField?.richValidationMessage ? toHTML(formField?.richValidationMessage, {
          components: serializers
        }) : '',
      }),
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.CHECKBOX]: {
      base: z.array(z.string()),
      required: (z: ZodArray<ZodString, 'many'>) => z.refine(value => value.some(item => item), formField.validation?.required),
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.RADIOS]: {
      base: z.string(),
      required: (z: ZodString) => z.min(1, formField.validation?.required),
      optional: (z: ZodString) => z.optional(),
    },
    [INPUT_TYPE.FILE]: {
      base: z.any(),
      required: (z: ZodAny) => z.refine((files) => files?.length == 1, formField.validation?.required),
      optional: (z: ZodString) => z.optional(),
      maxSize: (z: ZodAny) => z.refine((files) => files?.[0]?.size <= MAX_FILE_SIZE, formField.validation?.required),
      fileTypes: (z: ZodAny) => z.refine(
        (files) => ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type),
        formField.validation?.required
      ),
    },
    [INPUT_TYPE.NOTE]: {
      base: z.any(),
      required: (schema: ZodAny) => schema,
      optional: (schema: ZodAny) => schema.optional(),
    },
  };

  const validator = map[formField.inputType];
  if (!validator) {
    return undefined;
  }

  let validationObject = validator['base'];

  if (formField.required) {
    validationObject = validator['required'](validationObject);
  } else {
    validationObject = validator['optional'](validationObject);
  }

  return validationObject;
}

export function fieldToOptions(fieldOptions: FormFieldOptions = []) {
  return fieldOptions?.map((option) => ({
    title: option.title,
    value: option.value.current,
  }));
}
